// HvacDashboard — hero for AI Unit Specs + Today's PMs + Warranty Radar.
// Uses /hvac/units (returns { units, counts }).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import { toast } from "sonner";
import {
  Snowflake, Sparkle, MapPin, Clock, ArrowRight, CheckCircle, Warning,
  House, Camera, UploadSimple, ShieldCheck,
} from "@phosphor-icons/react";

const PM_CHIP = {
  overdue:   { cls: "bg-red-100 text-red-800",         label: "OVERDUE" },
  this_week: { cls: "bg-amber-100 text-amber-800",     label: "THIS WEEK" },
  soon:      { cls: "bg-cyan-100 text-cyan-800",       label: "SOON" },
  future:    { cls: "bg-slate-100 text-slate-700",     label: "FUTURE" },
  unset:     { cls: "bg-slate-100 text-slate-500",     label: "NO PM SET" },
};

export default function HvacDashboard() {
  const nav = useNavigate();
  const [units, setUnits] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/hvac/units");
        setUnits(r.data?.units || []);
        setCounts(r.data?.counts || {});
      } catch { toast.error("Could not load units"); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleDropPhoto = (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        sessionStorage.setItem("pending_hvac_nameplate_photo", JSON.stringify({
          name: f.name, type: f.type, data: reader.result,
        }));
        nav("/admin/hvac/units");
        toast.success("Nameplate photo attached — tap AI Unit Specs");
      } catch { toast.error("Could not attach photo"); }
    };
    reader.readAsDataURL(f);
  };

  const dueNow = units.filter(u => ["overdue","this_week"].includes(u.pm_status)).slice(0, 4);

  return (
    <AdminShell>
      <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-4" data-testid="hvac-dashboard">
        {/* HERO — Snap the nameplate */}
        <div
          onDragOver={e => e.preventDefault()}
          onDrop={handleDropPhoto}
          onClick={() => nav("/admin/hvac/units")}
          data-testid="hvac-hero-snap"
          className="cursor-pointer relative rounded-2xl overflow-hidden bg-gradient-to-br from-slate-900 via-cyan-900 to-slate-950 p-6 sm:p-8 text-white hover:shadow-xl transition group"
        >
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-6 right-6"><Snowflake weight="fill" size={200}/></div>
          </div>
          <div className="relative">
            <div className="text-[10px] font-black uppercase tracking-widest text-cyan-200 inline-flex items-center gap-1.5">
              <Sparkle weight="fill" size={11}/> Claude Vision · Nameplate OCR
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl leading-tight mt-2 tracking-tight" data-testid="hvac-hero-title">
              Snap the nameplate.<br/>
              <span className="text-cyan-300">I'll spec the unit.</span>
            </h1>
            <div className="text-sm text-cyan-100/80 mt-2 max-w-md">
              Drop a photo of the condenser or air handler nameplate — get tonnage, SEER, refrigerant, and warranty pre-filled in ~8 seconds.
            </div>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-cyan-400 text-slate-900 font-black uppercase text-[11px] tracking-widest group-hover:scale-105 transition">
                <UploadSimple weight="bold" size={14}/> Drop a photo
              </div>
              <div className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-white/10 border border-white/20 text-white font-black uppercase text-[11px] tracking-widest">
                <Camera weight="bold" size={14}/> or tap to open equipment
              </div>
            </div>
          </div>
        </div>

        {/* PMS DUE THIS WEEK */}
        <div className="rounded-xl bg-white border-2 border-slate-200 overflow-hidden" data-testid="hvac-pms-due">
          <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 inline-flex items-center gap-1.5">
              <Clock size={12}/> PMs due · this week + overdue
            </div>
            <button type="button" onClick={() => nav("/admin/hvac/units")}
              data-testid="hvac-all-units"
              className="text-[10px] font-black uppercase tracking-widest text-cyan-600 hover:text-cyan-800 inline-flex items-center gap-1">
              All equipment <ArrowRight size={10}/>
            </button>
          </div>
          {loading ? (
            <div className="p-6 text-sm text-slate-500 font-medium">Loading…</div>
          ) : dueNow.length === 0 ? (
            <div className="p-6 text-sm text-slate-500 font-medium" data-testid="hvac-no-pms">
              No PMs due this week. Nice cushion.
            </div>
          ) : (
            dueNow.map((u, i) => (
              <div key={u.id} className={`p-3 flex items-center gap-3 ${i > 0 ? "border-t border-slate-100" : ""}`}
                    data-testid={`hvac-unit-${u.id}`}>
                <div className="h-9 w-9 rounded-lg bg-cyan-50 text-cyan-600 flex items-center justify-center shrink-0">
                  <Snowflake weight="fill" size={16}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-sm truncate">{u.nickname || u.system_type} · {u.equipment_id || ""}</div>
                  <div className="text-[11px] text-slate-500 truncate inline-flex items-center gap-1">
                    <House size={10}/> {u.site_address || "—"}
                  </div>
                </div>
                <PmChip s={u.pm_status}/>
              </div>
            ))
          )}
        </div>

        {/* FUNNEL */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="hvac-funnel">
          <FunnelTile label="Overdue" value={counts.overdue || 0} icon={<Warning size={14}/>} accent="red"/>
          <FunnelTile label="This week" value={counts.this_week || 0} icon={<Clock size={14}/>} accent="amber"/>
          <FunnelTile label="Soon" value={counts.soon || 0} icon={<Snowflake size={14}/>} accent="cyan"/>
          <FunnelTile label="Under warranty" value={units.filter(u => u.warranty?.status === "active").length} icon={<ShieldCheck size={14}/>} accent="emerald"/>
        </div>
      </div>
    </AdminShell>
  );
}

function PmChip({ s }) {
  const m = PM_CHIP[s] || { cls: "bg-slate-100 text-slate-600", label: (s || "").toUpperCase() };
  return <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${m.cls}`}>{m.label}</span>;
}

function FunnelTile({ label, value, icon, accent }) {
  const bg = {
    red:     "bg-red-50 border-red-200 text-red-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    cyan:    "bg-cyan-50 border-cyan-200 text-cyan-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
  }[accent] || "bg-slate-50 border-slate-200 text-slate-800";
  return (
    <div className={`rounded-xl border-2 p-3 ${bg}`}>
      <div className="text-[9px] font-black uppercase tracking-widest opacity-70 inline-flex items-center gap-1">{icon} {label}</div>
      <div className="font-display font-black text-2xl mt-0.5">{value}</div>
    </div>
  );
}
